/**
 * Live-stats store — the feed seam for per-element telemetry (the details-panel
 * RED sections). Sibling to {@link ./liveTrafficStore}: the traffic store holds
 * the lean per-edge particle RATE (hot board path); this one holds the richer
 * per-edge + per-node stats (rate + error rate + latency percentiles) the
 * inspector reads when an element is selected.
 *
 * Keyed the SAME way as the overlay — edges by `trafficEdgeKey(from,to)`, nodes
 * by `normServiceName(svc)` — so a board module id ("Cart") and an OTel service
 * name ("cart") resolve to one key. Composable: every metric past `perSec` is
 * OPTIONAL; a source that supplies only rate leaves errorRate/latency undefined
 * and the sections render just what's present. Absent provider → NOOP → the
 * sections self-hide.
 */
import { createContext, useContext, useSyncExternalStore } from "react";
import { normServiceName, trafficEdgeKey } from "./liveTrafficStore.js";

export interface LatencyStat {
  readonly p50?: number;
  readonly p95?: number;
  readonly p99?: number;
}

/** Per-edge (directed service pair) live stats. */
export interface EdgeStat {
  /** Endpoint labels (post service→module remap) — for conformance enumeration
   *  of observed edges; the store key already encodes the normalized pair. */
  readonly from?: string;
  readonly to?: string;
  readonly perSec: number;
  readonly count?: number;
  /** Fraction 0..1 over the sampled traces (absent when no sample). */
  readonly errorRate?: number;
  readonly latencyMs?: LatencyStat;
  /** Sample size backing errorRate/latencyMs. */
  readonly sampled?: number;
}

/** Per-node (service) live stats. `perSec` is inbound rate. */
export interface NodeStat {
  readonly perSec: number;
  readonly errorRate?: number;
  readonly latencyMs?: LatencyStat;
  readonly sampled?: number;
}

export interface LiveStats {
  readonly edges: ReadonlyMap<string, EdgeStat>;
  readonly nodes: ReadonlyMap<string, NodeStat>;
}

export interface LiveStatsStore {
  subscribe(onChange: () => void): () => void;
  getStats(): LiveStats;
  /** Replace the whole snapshot (null clears). */
  setStats(stats: LiveStats | null): void;
}

const EMPTY: LiveStats = { edges: new Map(), nodes: new Map() };

export function createLiveStatsStore(): LiveStatsStore {
  let stats: LiveStats = EMPTY;
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getStats() {
      return stats;
    },
    setStats(next) {
      if (!next || (next.edges.size === 0 && next.nodes.size === 0)) {
        if (stats.edges.size === 0 && stats.nodes.size === 0) return;
        stats = EMPTY;
      } else {
        stats = next;
      }
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<LiveStatsStore | null>(null);
export const LiveStatsProvider = Ctx.Provider;

const NOOP: LiveStatsStore = {
  subscribe: () => () => {},
  getStats: () => EMPTY,
  setStats: () => {},
};

/** The ancestor-provided store, or null when no provider is mounted. */
export function useLiveStatsStore(): LiveStatsStore | null {
  return useContext(Ctx);
}

function useLiveStats(): LiveStats {
  const store = useContext(Ctx) ?? NOOP;
  return useSyncExternalStore(store.subscribe, store.getStats, store.getStats);
}

/** Live stats for a directed edge (endpoints are board module ids OR service
 *  names — both normalize to the same key). Null when none are known. */
export function useEdgeStat(from: string, to: string): EdgeStat | null {
  return useLiveStats().edges.get(trafficEdgeKey(from, to)) ?? null;
}

/** The whole per-edge stats map — for board layers that iterate all edges (the
 *  edge-health overlay). Empty (stable ref) when no telemetry is flowing. */
export function useLiveEdgeStats(): ReadonlyMap<string, EdgeStat> {
  return useLiveStats().edges;
}

/** Live stats for a node (board module id OR service name). Null when unknown. */
export function useNodeStat(moduleOrService: string): NodeStat | null {
  return useLiveStats().nodes.get(normServiceName(moduleOrService)) ?? null;
}
